import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { getProfilePics, getUserHubs } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { getProfilePicUrl } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { Actions, PhotoResolution } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { sanitizeFilters } from '@/Components/Common/FilterBar';
import { SESSION_STORAGE_KEYS } from '@/constants/device';
import useGetInventoryDevices from '../Hooks/useGetInventoryDevices';
import useUserInfo from '../Hooks/useUserInfo';
import HubDetails from '../HubDetails/HubDetails';
import AddAccount from './AddAccount';
import DevicesComponent from './Devices.component';
import {
  buildInventoryQueryParams,
  getInventoryFilterGroups,
  INVENTORY_PAGE_SIZE,
  INVENTORY_UNPAGED_LIMIT,
} from './inventoryFilters';

const EMPTY_DEVICES = [];

const Devices = () => {
  const location = useLocation();
  const routeCount = location.state?.count;
  const currentUser = useSelector(getCurrentUser);
  const { useruuid, providerid: providerId } = useMemo(() => currentUser, [currentUser]);
  const isStaff = currentUser?.permission_level === 'Staff';
  useUserInfo({ useruuid });

  // Router state is only a first guess; the API total switches to paged mode when needed.
  const [isPaged, setIsPaged] = useState(routeCount > INVENTORY_UNPAGED_LIMIT);
  const [limit, setLimit] = useState(isPaged ? INVENTORY_PAGE_SIZE : INVENTORY_UNPAGED_LIMIT);
  const [offset, setOffset] = useState(0);

  const [action, setAction] = useState(null);
  const [record, setRecord] = useState(null);
  const [queryParams, setQueryParams] = useState(() =>
    buildInventoryQueryParams(
      sanitizeFilters(
        SessionStorage.get(SESSION_STORAGE_KEYS.INVENTORY_FILTER),
        getInventoryFilterGroups(isStaff)
      )
    )
  );
  const navigationKey = true;
  const queryString = new URLSearchParams(queryParams).toString();

  const handleQueryParamsChange = useCallback((params) => {
    setQueryParams((prev) =>
      new URLSearchParams(prev).toString() === new URLSearchParams(params).toString()
        ? prev
        : params
    );
  }, []);

  const [lastQueryString, setLastQueryString] = useState(queryString);
  if (lastQueryString !== queryString) {
    setLastQueryString(queryString);
    setOffset(0);
  }

  const userHubs = useSelector(getUserHubs(useruuid));
  const resourceMap = useSelector(getProfilePics());
  const userData = useSelector((user) => user.data.userInfo);
  const updatedUserInfo = userData[useruuid];
  const mUserHubs = useMemo(() => {
    return userHubs.map((hub) => ({
      ...hub,
      profilePic64: getProfilePicUrl(hub.useruuid, resourceMap, PhotoResolution.R64),
    }));
  }, [userHubs]);

  // Staff users may only see devices of the accounts they can access.
  const allowedDeviceUuids = useMemo(
    () => (isStaff ? mUserHubs.map((hub) => hub.deviceuuid).filter(Boolean) : null),
    [isStaff, mUserHubs]
  );

  const {
    data: inventoryData,
    isFetching: fetchingDeviceData,
    refetch: fetchDevices,
  } = useGetInventoryDevices({
    useruuid,
    queryParams,
    providerId,
    offset,
    limit,
    allowedDeviceUuids,
  });
  const inventoryDevices = inventoryData?.devices ?? EMPTY_DEVICES;
  const total = inventoryData?.total ?? null;

  // Keep the last known count while a new page/query is loading.
  const [resultCount, setResultCount] = useState(routeCount ?? 0);
  if (total != null && total !== resultCount) {
    setResultCount(total);
  }
  if (!isPaged && total != null && total > limit) {
    setIsPaged(true);
    setLimit(INVENTORY_PAGE_SIZE);
    setOffset(0);
  }
  // A refresh can shrink the result set below the current page.
  if (isPaged && total != null && offset > 0 && offset >= total) {
    setOffset(Math.floor(Math.max(total - 1, 0) / limit) * limit);
  }

  /* handler functions */
  const onActionClick = (action, record, offset, limit) => () => {
    let resolvedRecord = record;
    if (action === Actions.listHub && record?.deviceuuid) {
      const matchedHub = mUserHubs.find((hub) => hub.deviceuuid === record.deviceuuid);
      if (matchedHub) {
        resolvedRecord = matchedHub;
      }
    }
    setAction(action);
    setRecord(resolvedRecord);
    setOffset(offset);
    setLimit(limit);
    fetchDevices();
  };

  const onSubmit = ({ update = true, redirect = true } = {}) => {
    update && fetchDevices();
    redirect && setAction(null);
    redirect && setRecord(null);
  };

  return !action ? (
    <DevicesComponent
      providerId={providerId}
      fetchDevices={fetchDevices}
      deviceData={inventoryDevices}
      fetchingDeviceData={fetchingDeviceData}
      onActionClick={onActionClick}
      // onSubmit={onSubmit}
      deviceCount={resultCount}
      showPagination={isPaged}
      offset={offset}
      limit={limit}
      userHubsData={mUserHubs}
      setQueryParams={handleQueryParamsChange}
    />
  ) : action === Actions.listHub ? (
    <HubDetails
      onActionClick={onActionClick}
      hubRecord={record}
      userHubsData={mUserHubs}
      offset={offset}
      limit={limit}
      openSubMenu={true}
      navigationKey={navigationKey}
      sendMsgDisplay={true}
      showContactingStaff={true}
      fetchDevices={fetchDevices}
      deviceData={inventoryDevices}
      fetchingDeviceData={fetchingDeviceData}
      showPagination={isPaged}
      loginUserTimeformat={{ is24hoursformat: updatedUserInfo?.is24hoursformat }}
      loginUserMeasurementUnit={{ measurement: updatedUserInfo?.measurement }}
    />
  ) : action === Actions.assignDevice ? (
    <AddAccount onActionClick={onActionClick} deviceRecord={record} offset={offset} limit={limit} />
  ) : (
    <></>
  );
};

Devices.propTypes = {};

export default Devices;
